import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaStore, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import { Container, Row, Col } from 'react-bootstrap';
import '../styles/GroceryStoreDetail.css';
import ItemModal from '../components/ItemModal';
import { formatTodayHours } from '../utils/formatTodayHours';
import { StoreContext } from '../context/StoreContext';
import { isStoreOpen } from '../utils/isStoreOpen';
import { fetchGroceryStoreById } from '../api/apiService';
import { formatPrice } from '../utils/formatPrice';
// ✅ UPDATE 1: Import Hook
import { useClosingStatus } from '../utils/useClosingStatus';

const GROCERY_CATEGORIES = [
    { label: 'All', value: 'all' },
    { label: 'Fruits', value: 'fruits' },
    { label: 'Vegetables', value: 'vegetables' },
    { label: 'Dairy', value: 'dairy' },
    { label: 'Snacks', value: 'snacks' },
    { label: 'Beverages', value: 'beverages' },
    { label: 'Bakery', value: 'bakery' },
    { label: 'Frozen', value: 'frozen' },
    { label: 'Staples', value: 'staples' },
    { label: 'Personal Care', value: 'personalcare' },
    { label: 'Household', value: 'household' },
    { label: 'Other', value: 'other' },
];

const GroceryStoreDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { cart, addItemToCart, updateItemQuantity } = useContext(StoreContext);
    const [store, setStore] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const loadStoreDetails = async () => {
            if (!id) {
                setError("Store ID is missing.");
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const response = await fetchGroceryStoreById(id);
                setStore(response.data);
            } catch (err) {
                setError('Failed to load store details.');
            } finally {
                setLoading(false);
            }
        };
        loadStoreDetails();
    }, [id]);

    // ✅ UPDATE 2: Prepare timings object for the hook
 
    const timings = React.useMemo(() => {
        return store?.operatingHours || store?.timings || null;
    }, [store]);
	
    const { isClosingSoon, minutesLeft, nextDayDeliveryTime } = useClosingStatus(
  timings,
  store?.location?.country
);

    const handleOpenModal = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleItemAdd = (itemWithQuantityAndNotes) => {
        addItemToCart({
            ...itemWithQuantityAndNotes,
            item_id: itemWithQuantityAndNotes._id,
            vendor_id: store._id,
            vendor_type: 'grocery_store',
        });
        setShowModal(false);
    };

    const business = store;
    const currency = business?.currency;
    const currencySymbol = currency === 'CAD' ? 'CAD' : (currency === 'USD' ? '$' : '₹');

    if (loading) {
        return <div className="detail-container loading-state"><FaSpinner className="spinner" /> Loading store...</div>;
    }

    if (error || !store) {
        return (
            <div className="detail-container error-state">
                <p className="error-message">{error || "Store not found."}</p>
                <Link to="/" className="back-link"><FaArrowLeft /> Back to Home</Link>
            </div>
        );
    }

    const bannerImg = store.image || 'https://via.placeholder.com/600x200?text=Grocery+Store';
    const locationStr = store.location
        ? [store.location.street, store.location.city, store.location?.state].filter(Boolean).join(', ')
        : '';

    // ✅ FIX: Renamed variable 'open' to 'isOpen' to avoid global conflict
     // ✅ FIX: Pass the memoized smart timings variable
    const isOpen = isStoreOpen(
  timings,
  store?.location?.country
);


    const storeItems = Array.isArray(store.items) ? store.items : [];

    // ✅ FIX: Ensure filteredItems is defined BEFORE return
    const filteredItems = storeItems.filter(item => {
        if (item.isAvailable !== true) return false;
        if (selectedCategory === 'all') return true;
        const type = (item.type || '').toLowerCase().replace(/[\s-]/g, '');
        return type === selectedCategory.replace(/[\s-]/g, '');
    });

    return (
        <motion.div
            className="grocery-store-detail-page"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="detail-header">
                <button className="back-button" onClick={() => navigate(-1)}><FaArrowLeft /></button>
                <img src={bannerImg} alt={store.name} className="detail-banner-img" />
                <div className="detail-overlay">
                    <h1 className="detail-name">{store.name}</h1>
                    <p className="detail-info"><FaStore /> Grocery Store • <FaMapMarkerAlt /> {locationStr}</p>
                </div>
            </div>

            {/* ✅ UPDATE 3: Insert Banner Here (Between Header and Content) */}
            {isClosingSoon && (
                <div className="closing-banner-wrapper">
                    <motion.div 
                        className="closing-alert-banner"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="closing-alert-content">
                            <span className="closing-icon">⚠️</span>
                            <div className="closing-text">
                                <strong>Closing in {minutesLeft} minutes!</strong>
                                <span>Orders placed now will be delivered tomorrow by <b>{nextDayDeliveryTime}</b>.</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            <Container style={{ maxWidth: 1100, padding: 0 }}>
                <div className="detail-info-bar">
                    <div className="detail-info-box timings">
                        <span className="detail-info-label">Timings</span>
                        <span className="detail-info-value">
                            {formatTodayHours(timings)}
                        </span>
                    </div>

                    
                    {/* ✅ FIX: Use 'isOpen' here */}
                    <span className={`status-badge ${isOpen ? 'open' : 'closed'}`}>
                        {isOpen ? 'Opened' : 'Closed'}
                    </span>

                    <div className="detail-info-box delivery">
                        <span className="detail-info-label">Delivery Time</span>
                        <span className="detail-info-value">{store.deliveryTime || 'N/A'}</span>
                    </div>
                </div>

                <div className="detail-content">
                    <h2 className="menu-heading">Available Items</h2>
                    <div className="menu-category-tabs">
                        {GROCERY_CATEGORIES.map(cat => (
                            <button
                                key={cat.value}
                                className={`menu-category-tab${selectedCategory === cat.value ? ' active' : ''}`}
                                onClick={() => setSelectedCategory(cat.value)}
                            >
                                {cat.label}
                            </button>
                        ))}
                    </div>

                    {filteredItems && filteredItems.length > 0 ? (
                        <Row className="g-3"> 
                            {filteredItems.map(item => {
                                const itemId = item._id?.$oid || item._id?.toString?.() || item._id;
                                const cartItem = cart?.items?.find(ci => ci.item_id?.toString() === itemId?.toString());
                                const qty = cartItem ? cartItem.quantity : 0;
                                
                                return (
                                    <Col key={itemId} xs={6} sm={6} md={4} lg={3}>
                                        <motion.div
                                            className="menu-item-card"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{ duration: 0.3 }}
                                            whileHover={{ scale: 1.03 }}
                                            whileTap={{ scale: 0.97 }}
                                            // ✅ FIX: Use 'isOpen' here
                                            style={{ opacity: isOpen ? 1 : 0.7, pointerEvents: isOpen ? 'auto' : 'none' }}
                                            onClick={() => isOpen && handleOpenModal(item)}
                                        >
                                            <img src={item.image || 'https://via.placeholder.com/150'} alt={item.name} className="menu-item-img" />
                                            <div className="menu-item-info">
                                                <h3 className="menu-item-name">{item.name}</h3>
                                                <div className="menu-item-footer">
                                                    <span className="menu-item-price">
                                                        {formatPrice(item.price, currency, currencySymbol)}
                                                    </span>
                                                    {qty === 0 ? (
                                                        <button
                                                            className="add-to-cart-btn"
                                                            // ✅ FIX: Use 'isOpen' here
                                                            disabled={!isOpen}
                                                            onClick={(e) => { e.stopPropagation(); if (isOpen) handleItemAdd({ ...item, quantity: 1 }); }}
                                                        >
                                                            Add to Cart
                                                        </button>
                                                    ) : (
                                                        <div className="qty-controls" onClick={(e) => e.stopPropagation()}>
                                                            <button className="qty-btn" onClick={() => { if (qty > 1) updateItemQuantity(cartItem._id, qty - 1); else updateItemQuantity(cartItem._id, 0); }}>−</button>
                                                            <span className="qty-value">{qty}</span>
                                                            <button className="qty-btn" onClick={() => updateItemQuantity(cartItem._id, qty + 1)}>+</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    </Col>
                                );
                            })}
                        </Row>
                    ) : (
                        <p className="no-menu-items">No items available in this category.</p>
                    )}
                </div>
            </Container>

            {showModal && selectedItem && (
                <ItemModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    item={selectedItem}
                    handleAddToCart={handleItemAdd}
                    cart={cart}
                    vendor_id={store._id}
                    vendor_type="grocery_store"
                    currency={currency}
                    currencySymbol={currencySymbol}
                />
            )}
        </motion.div>
    );
};

export default GroceryStoreDetail;
